import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Repeat2, Link2, Search, X } from "lucide-react";
import { usePagedList, PaginationBar } from "@/components/Pagination";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [tiposRet, setTiposRet] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState("");
  const filteredItems = React.useMemo(() => {
    const norm = (v) => (v || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const q = norm(search.trim());
    if (!q) return items;
    return items.filter(it => norm(`${it.razon_social} ${it.nombre_comercial} ${it.numero_identificacion} ${it.contacto} ${it.email} ${it.ciudad} ${it.telefono}`).includes(q));
  }, [items, search]);
  const pagedSuppliers = usePagedList(filteredItems, 20);
  function emptyForm() {
    return {
      tipo_identificacion: "ruc", numero_identificacion: "", tipo_persona: "juridica",
      razon_social: "", nombre_comercial: "",
      tipo_proveedor_id: "", direccion: "", ciudad: "", provincia: "",
      telefono: "", email: "", contacto: "",
      forma_pago_id: "", dias_credito: 0,
      limite_credito: "", cuenta_contable_id: "", contraparte_cliente_id: "",
      acepta_devoluciones: true, dias_antes_vencimiento: 90,
      politica_observaciones: "", retenciones_default: [], activo: true,
    };
  }

  const load = async () => {
    try {
      const [pr, tp, fp, cc, tr] = await Promise.all([
        api.get("/masters/proveedores"),
        api.get("/masters/catalog/tipos_proveedor"),
        api.get("/masters/catalog/formas_pago"),
        api.get("/masters/cuentas-auxiliares"),
        api.get("/tipos-retencion").catch(() => ({ data: [] })),
      ]);
      setItems(pr.data); setTipos(tp.data); setFormasPago(fp.data); setCuentas(cc.data || []);
      setTiposRet(tr.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {...form};
      ["tipo_proveedor_id", "forma_pago_id", "cuenta_contable_id", "contraparte_cliente_id"].forEach(k => { if (!payload[k]) payload[k] = null; });
      if (payload.limite_credito === "" || payload.limite_credito === null) payload.limite_credito = null;
      else payload.limite_credito = parseFloat(payload.limite_credito);
      // Limpiar retenciones default: dejar solo las que tengan código y porcentaje válido
      payload.retenciones_default = (payload.retenciones_default || [])
        .filter(r => r.codigo_sri && (parseFloat(r.porcentaje) || 0) >= 0)
        .map(r => ({ tipo: r.tipo, codigo_sri: r.codigo_sri, porcentaje: parseFloat(r.porcentaje) || 0 }));
      if (editing) await api.patch(`/masters/proveedores/${editing.id}`, payload);
      else await api.post("/masters/proveedores", payload);
      toast.success("Guardado"); setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.razon_social}"?`)) return;
    try { await api.delete(`/masters/proveedores/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const replicarCliente = async (it) => {
    if (it.contraparte_cliente_id) {
      toast.info(`Ya está vinculado al cliente ${it.contraparte_cliente_id.slice(0,8)}…`);
      return;
    }
    if (!window.confirm(`¿Crear ficha de cliente espejo para "${it.razon_social}"?\nLos archivos quedarán vinculados pero separados.`)) return;
    try {
      const { data } = await api.post(`/masters/proveedores/${it.id}/replicar-cliente`);
      toast.success(data.ya_existia ? "Ya existía, vinculado correctamente" : "Cliente creado y vinculado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Maestros</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Proveedores</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Configure la política de devoluciones por caducidad de cada proveedor.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-supplier-btn">
          <Plus size={16} className="mr-2" /> Nuevo proveedor
        </Button>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-[#d1dbd6] bg-[#f7f9f8] flex items-center gap-2">
          <Search size={14} className="text-[#5c7368] flex-shrink-0" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por razón social, nombre comercial, RUC, contacto, email o ciudad…"
            className="flex-1 h-8 bg-transparent text-sm focus:outline-none placeholder:text-[#8ba397]"
            data-testid="suppliers-search-input" />
          {search && (
            <button type="button" onClick={() => setSearch("")} className="text-[#5c7368] hover:text-[#15241d]" title="Limpiar búsqueda" data-testid="suppliers-search-clear">
              <X size={14} />
            </button>
          )}
          <span className="text-[11px] text-[#5c7368] tabular-nums" data-testid="suppliers-search-count">{filteredItems.length} / {items.length}</span>
        </div>
        <table className="erp-table">
          <thead><tr><th>Identificación</th><th>Razón social</th><th>Contacto</th><th>Política devolución</th><th>Estado</th><th className="w-24"></th></tr></thead>
          <tbody>
            {pagedSuppliers.paged.map(it => (
              <tr key={it.id} data-testid={`supplier-row-${it.id}`}>
                <td className="font-mono text-xs">{it.numero_identificacion}</td>
                <td className="font-medium">{it.razon_social}</td>
                <td>{it.contacto || "—"}</td>
                <td>{it.acepta_devoluciones ? <Badge variant="secondary">{it.dias_antes_vencimiento}d antes</Badge> : <span className="text-xs text-[#8ba397]">No acepta</span>}</td>
                <td>{it.activo ? <Badge className="fefo-safe border">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => replicarCliente(it)} title={it.contraparte_cliente_id ? "Ya vinculado a cliente" : "Replicar como cliente"} data-testid={`replicate-cli-${it.id}`}>
                      {it.contraparte_cliente_id ? <Link2 size={16} className="text-[#205c45]" /> : <Repeat2 size={16} className="text-[#5c7368]" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(it); setForm({...emptyForm(), ...it}); setDialogOpen(true); }}><Edit2 size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredItems.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]" data-testid="suppliers-empty">{search ? `Sin coincidencias para "${search}"` : "Sin proveedores registrados"}</td></tr>}
          </tbody>
        </table>
        <PaginationBar page={pagedSuppliers.page} total={pagedSuppliers.total} pageSize={pagedSuppliers.pageSize} onPrev={pagedSuppliers.prev} onNext={pagedSuppliers.next} label="proveedores" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} proveedor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo ID</Label>
              <Select value={form.tipo_identificacion} onValueChange={(v) => setForm({...form, tipo_identificacion: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ruc">RUC</SelectItem><SelectItem value="cedula">Cédula</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Número</Label><Input value={form.numero_identificacion} onChange={(e) => setForm({...form, numero_identificacion: e.target.value})} /></div>
            <div className="col-span-2"><Label>Razón social</Label><Input value={form.razon_social} onChange={(e) => setForm({...form, razon_social: e.target.value})} data-testid="supplier-razon-input" /></div>
            <div><Label>Nombre comercial</Label><Input value={form.nombre_comercial} onChange={(e) => setForm({...form, nombre_comercial: e.target.value})} /></div>
            <div>
              <Label>Tipo persona</Label>
              <Select value={form.tipo_persona} onValueChange={(v) => setForm({...form, tipo_persona: v})}>
                <SelectTrigger data-testid="supplier-tipo-persona"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="juridica">Jurídica</SelectItem>
                  <SelectItem value="natural">Natural</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo proveedor</Label>
              <Select value={form.tipo_proveedor_id || ""} onValueChange={(v) => setForm({...form, tipo_proveedor_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Dirección</Label><Input value={form.direccion} onChange={(e) => setForm({...form, direccion: e.target.value})} /></div>
            <div><Label>Ciudad</Label><Input value={form.ciudad} onChange={(e) => setForm({...form, ciudad: e.target.value})} data-testid="supplier-ciudad" /></div>
            <div><Label>Provincia</Label><Input value={form.provincia} onChange={(e) => setForm({...form, provincia: e.target.value})} data-testid="supplier-provincia" /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Contacto</Label><Input value={form.contacto} onChange={(e) => setForm({...form, contacto: e.target.value})} /></div>
            <div>
              <Label>Forma de pago</Label>
              <Select value={form.forma_pago_id || ""} onValueChange={(v) => setForm({...form, forma_pago_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{formasPago.map(fp => <SelectItem key={fp.id} value={fp.id}>{fp.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Días crédito</Label><Input type="number" value={form.dias_credito} onChange={(e) => setForm({...form, dias_credito: parseInt(e.target.value)||0})} /></div>
            <div><Label>Límite crédito (USD)</Label><Input type="number" step="0.01" value={form.limite_credito} onChange={(e) => setForm({...form, limite_credito: e.target.value})} placeholder="Tope a deber" data-testid="supplier-limite-credito" /></div>
            <div>
              <Label>Cuenta contable (CxP)</Label>
              <Select value={form.cuenta_contable_id || ""} onValueChange={(v) => setForm({...form, cuenta_contable_id: v})}>
                <SelectTrigger data-testid="supplier-cuenta-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="max-h-72">{cuentas.filter(c => c.tipo === "pasivo").map(c => <SelectItem key={c.id} value={c.id}>{c.codigo} · {c.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 border-t pt-3 mt-2">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Política de devoluciones</div>
              <div className="flex items-center gap-2 mb-2"><Checkbox checked={form.acepta_devoluciones} onCheckedChange={(v) => setForm({...form, acepta_devoluciones: !!v})} /><Label>Acepta devolución por caducidad</Label></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Días antes del vencimiento</Label><Input type="number" value={form.dias_antes_vencimiento} onChange={(e) => setForm({...form, dias_antes_vencimiento: parseInt(e.target.value)||0})} /></div>
                <div><Label>Observaciones</Label><Input value={form.politica_observaciones} onChange={(e) => setForm({...form, politica_observaciones: e.target.value})} /></div>
              </div>
            </div>

            <div className="col-span-2 border-t pt-3 mt-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Retenciones SRI por defecto</div>
                <Button size="sm" variant="outline" type="button" data-testid="add-ret-default-btn"
                        onClick={() => setForm(f => ({ ...f, retenciones_default: [...(f.retenciones_default || []), { tipo: "renta", codigo_sri: "", porcentaje: 0 }] }))}>
                  <Plus size={12} className="mr-1" /> Agregar
                </Button>
              </div>
              <p className="text-[11px] text-[#5c7368] mb-2">
                Códigos que se sugerirán automáticamente al generar retención de gastos operativos de este proveedor. Puede dejar vacío para usar los códigos genéricos.
              </p>
              {(form.retenciones_default || []).length === 0 && (
                <div className="text-xs italic text-[#8ba397] py-2">Sin retenciones predefinidas. Se usarán las genéricas (IR 1% / 2%).</div>
              )}
              {(form.retenciones_default || []).map((r, idx) => {
                const opciones = tiposRet.filter(t => t.tipo === r.tipo);
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                    <div className="col-span-3">
                      <Label className="text-[10px]">Tipo</Label>
                      <Select value={r.tipo} onValueChange={(v) => setForm(f => {
                        const arr = [...(f.retenciones_default || [])];
                        arr[idx] = { ...arr[idx], tipo: v, codigo_sri: "", porcentaje: 0 };
                        return { ...f, retenciones_default: arr };
                      })}>
                        <SelectTrigger data-testid={`ret-def-tipo-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="renta">Renta</SelectItem>
                          <SelectItem value="iva">IVA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-7">
                      <Label className="text-[10px]">Código SRI</Label>
                      <Select value={r.codigo_sri} onValueChange={(v) => {
                        const t = opciones.find(o => o.codigo_sri === v);
                        setForm(f => {
                          const arr = [...(f.retenciones_default || [])];
                          arr[idx] = { ...arr[idx], codigo_sri: v, porcentaje: t?.porcentaje ?? arr[idx].porcentaje };
                          return { ...f, retenciones_default: arr };
                        });
                      }}>
                        <SelectTrigger data-testid={`ret-def-codigo-${idx}`}>
                          <SelectValue placeholder="Elija código…" />
                        </SelectTrigger>
                        <SelectContent>
                          {opciones.map(o => (
                            <SelectItem key={o.id} value={o.codigo_sri}>
                              <span className="font-mono">{o.codigo_sri}</span> · {o.porcentaje}% — {o.descripcion}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Label className="text-[10px]">%</Label>
                      <Input type="number" step="0.01" value={r.porcentaje}
                             onChange={(e) => setForm(f => {
                               const arr = [...(f.retenciones_default || [])];
                               arr[idx] = { ...arr[idx], porcentaje: parseFloat(e.target.value) || 0 };
                               return { ...f, retenciones_default: arr };
                             })}
                             className="h-9 text-xs tabular-nums" data-testid={`ret-def-pct-${idx}`} />
                    </div>
                    <div className="col-span-1">
                      <Button size="icon" variant="ghost" type="button" onClick={() => setForm(f => ({
                        ...f, retenciones_default: (f.retenciones_default || []).filter((_, i) => i !== idx),
                      }))} data-testid={`ret-def-del-${idx}`}>
                        <Trash2 size={14} className="text-[#b53a25]" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-supplier-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
